import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CommonService } from '../../../../services/common/common.service';
import { UsersDataService, UsersColumnsService } from '../../../services';
import { User, UsersResult } from '../../../interfaces';
import { DialogFilerComponent } from '../../filer/filer.component';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  providers: [MessageService, ConfirmationService, DynamicDialogRef, DynamicDialogConfig],
})
export class UserFormComponent implements OnInit, OnDestroy, AfterViewInit {

  public user: User = {};
  public userForm: FormGroup = new FormGroup({});
  public users: User[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};
  public isLoading: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  public redirect = true;
  public disableSubmit = false;
  public enableFormAlert = true;

  private filerDialog: DynamicDialogRef = new DynamicDialogRef();

  constructor(
    private translate: TranslateService,
    public common: CommonService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private usersDataService: UsersDataService,
    public usersColumnsService: UsersColumnsService,
  ) {
  }

  ngOnInit(): void {
    console.log('ngOnInit', 'UserFormComponent');
    this.route.params.subscribe((params: any) => {
      if (typeof params['id'] !== 'undefined') {
        this.titleService.setTitle('User - Edit');
        this.getUser(parseInt(params['id'], 10));
      }
    });
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.user !== 'undefined') {
      this.user = this.dynamicDialogConfig.data.user;
      this.user.password = '.';
    }
    if (typeof this.user.id === 'undefined' || this.user.id === null) {
      this.user.id = null;
      this.user.birthDate = moment().set({minute: 0, second: 0, millisecond: 0}).format('YYYY-MM-DD\THH:mm:ssZ');
      this.user.enabled = false;
      this.user.locked = false;
      this.user.expired = false;
    }
    this.getUsers();
    this.prepareFrom();
  }

  ngOnDestroy(): void {
    console.log('ngOnDestroy', 'UserFormComponent');
  }

  ngAfterViewInit(): void {
    console.log('ngAfterViewInit', 'UserFormComponent');
  }

  getUser(id: number): void {
    this.isLoading = true;
    this.usersDataService.getUser(id).subscribe((response: User|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.user = response;
        this.user.password = '.';
        this.prepareFrom();
      }
    });
  }

  getUsers(): void {
    if (this.dynamicDialogConfig.data && typeof this.dynamicDialogConfig.data.users !== 'undefined' && this.dynamicDialogConfig.data.users.length > 0) {
      this.users = this.dynamicDialogConfig.data.users;
      return;
    }
    this.usersDataService.getUsers(this.parameters).subscribe((response: UsersResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.users = response.results;
      }
    });
  }

  prepareFrom(): void {
    this.user.birthDate = moment(this.user.birthDate).toDate();
    this.userForm = new FormGroup({
      firstName: new FormControl(this.user.firstName, []),
      lastName: new FormControl(this.user.lastName, []),
      gender: new FormControl(this.user.gender, []),
      picture: new FormControl(this.user.picture, []),
      birthDate: new FormControl(this.user.birthDate, []),
      profile: new FormControl(this.user.profile, []),
      address: new FormControl(this.user.address, []),
      zipCode: new FormControl(this.user.zipCode, []),
      country: new FormControl((typeof this.user.country === 'number') ? this.user.country : this.user.country?.id, []),
      city: new FormControl((typeof this.user.city === 'number') ? this.user.city : this.user.city?.id, []),
      company: new FormControl((typeof this.user.company === 'number') ? this.user.company : this.user.company?.id, []),
      job: new FormControl(this.user.job, []),
      language: new FormControl((typeof this.user.language === 'number') ? this.user.language : this.user.language?.id, []),
      type: new FormControl(this.user.type, []),
      enabled: new FormControl(this.user.enabled, [Validators.required, ]),
      username: new FormControl(this.user.username, [Validators.required, ]),
      password: new FormControl(this.user.password, [Validators.required, ]),
      phone: new FormControl(this.user.phone, []),
      email: new FormControl(this.user.email, [Validators.required, ]),
      salt: new FormControl(this.user.salt, []),
      facebookKey: new FormControl(this.user.facebookKey, []),
      locked: new FormControl(this.user.locked, [Validators.required, ]),
      expired: new FormControl(this.user.expired, [Validators.required, ]),
      id: new FormControl(this.user.id, []),
      roles: new FormControl(this.user.roles, [Validators.required, ]),
    });
  }

  list() {
    this.router.navigate(['/users']);
  }

  changeCountry(event: any) {
    console.log('changeCountry', event);
  }

  showErrors() {
      this.showErrors();
      const invalidFields: string[] = [];
      Object.keys(this.userForm.controls).forEach((field: any) => {
        const control = this.userForm.get(field);
        if (control !== null) {
          control.markAsTouched({ onlySelf: true });
          if (control.invalid) {
            invalidFields.push(field);
          }
        }
      });
  }

  submitForm(redirect: boolean) {
    this.redirect = redirect;
    if (!this.userForm.valid) {
      this.showErrors();
    } else {
      this.saveData(redirect);
    }
  }

  saveData(redirect: boolean) {
    this.user = this.userForm.value;
    this.disableSubmit = true;
    console.log('user', this.user);
    if (typeof this.user.id !== 'undefined' && this.user.id !== null) {
      this.add(redirect);
    } else {
      this.update(redirect);
    }
    return false;
  }

  add(redirect: boolean) {
    this.usersDataService.updateUser(this.user).subscribe((data: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('USERUPDATED')});
      if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
        this.dynamicDialogRef.close({refresh: true});
      } else if (redirect) {
        this.list();
      }
    }, (error: any) => {
      this.disableSubmit = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERNOTUPDATED') + ' ' + error.message});
      console.warn(error);
    });
  }

  update(redirect: boolean) {
    this.usersDataService.addUser(this.user).subscribe((data: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: this.translate.instant('USERCREATED')});
        if (redirect && typeof this.dynamicDialogConfig.data !== 'undefined') {
          this.dynamicDialogRef.close({refresh: true});
        } else if (redirect) {
          this.list();
        }
      }, (error: any) => {
        this.disableSubmit = false;
        this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('USERNOTCREATED') + ' ' + error.message});
        console.warn(error);
    });
  }

  openFilerDialog(field: string): void {
    let url = '';
    const control = this.userForm.get(field);
    if (control !== null) {
      url = control.value;
    }
    this.filerDialog = this.dialogService.open(DialogFilerComponent, {
      header: '', // this.translate.instant('FILEMANAGER'),
      width: '90%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1500,
      data: {
        path: 'users',
        field: field,
        url: url,
      },
    });
    this.filerDialog.onClose.subscribe((response: any) => {
      if (response && response.selected) {
        const control = this.userForm.get(field);
        if (control !== null) {
          control.setValue(response.selected.webPath);
        }
      }
    });
  }

}

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class DialogUserFormComponent extends UserFormComponent {
}

